import { useState, useEffect, useCallback } from 'react'
import { userCategoriesApi, type UserCategory } from '../api/team'
import { UserCategoryAdminPanel } from '../components/UserCategoryAdminPanel'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Checkbox,
  Icon,
  useSnackbar,
  useConfirm,
  FormSection,
} from '../components/ui'

interface CategoryFormState {
  name: string
  slug: string
  description: string
  color: string
  sortOrder: number
  canSeeAllContracts: boolean
  canSeeSubordinates: boolean
  canSeeRealAmounts: boolean
  canSeeTotalCommissions: boolean
  canManageUsers: boolean
  canManageProducts: boolean
  canManagePipelines: boolean
  canManageForms: boolean
}

const EMPTY_FORM: CategoryFormState = {
  name: '',
  slug: '',
  description: '',
  color: '#6366f1',
  sortOrder: 0,
  canSeeAllContracts: false,
  canSeeSubordinates: false,
  canSeeRealAmounts: false,
  canSeeTotalCommissions: false,
  canManageUsers: false,
  canManageProducts: false,
  canManagePipelines: false,
  canManageForms: false,
}

const PERMISSION_FIELDS: { key: keyof Pick<
  CategoryFormState,
  'canSeeAllContracts' | 'canSeeSubordinates' | 'canSeeRealAmounts' | 'canSeeTotalCommissions' |
  'canManageUsers' | 'canManageProducts' | 'canManagePipelines' | 'canManageForms'
>; label: string }[] = [
  { key: 'canSeeAllContracts', label: 'Vede tutti i contratti' },
  { key: 'canSeeSubordinates', label: 'Vede i subordinati' },
  { key: 'canSeeRealAmounts', label: 'Vede gli importi reali' },
  { key: 'canSeeTotalCommissions', label: 'Vede le commissioni totali' },
  { key: 'canManageUsers', label: 'Gestisce gli utenti' },
  { key: 'canManageProducts', label: 'Gestisce i prodotti' },
  { key: 'canManagePipelines', label: 'Gestisce le pipeline' },
  { key: 'canManageForms', label: 'Gestisce i moduli' },
]

function nameToSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

export function UserCategoriesPage() {
  const rolesOn = useFeatureFlag('ruoli.attivazione_copia')
  const askConfirm = useConfirm()
  const snackbar = useSnackbar()
  const [items, setItems] = useState<UserCategory[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<CategoryFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await userCategoriesApi.list()
      setItems(res)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il caricamento')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  function openCreate() {
    setForm(EMPTY_FORM)
    setEditId(null)
    setSaveError('')
    setShowModal(true)
  }

  function openEdit(cat: UserCategory) {
    setForm({
      name: cat.name,
      slug: cat.slug,
      description: cat.description ?? '',
      color: cat.color ?? '#6366f1',
      sortOrder: cat.sortOrder ?? 0,
      canSeeAllContracts: cat.canSeeAllContracts ?? false,
      canSeeSubordinates: cat.canSeeSubordinates ?? false,
      canSeeRealAmounts: cat.canSeeRealAmounts ?? false,
      canSeeTotalCommissions: cat.canSeeTotalCommissions ?? false,
      canManageUsers: cat.canManageUsers ?? false,
      canManageProducts: cat.canManageProducts ?? false,
      canManagePipelines: cat.canManagePipelines ?? false,
      canManageForms: cat.canManageForms ?? false,
    })
    setEditId(cat.id)
    setSaveError('')
    setShowModal(true)
  }

  function closeModal() {
    setShowModal(false)
    setEditId(null)
    setSaveError('')
  }

  function handleNameChange(name: string) {
    setForm(f => ({ ...f, name, slug: nameToSlug(name) }))
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setSaveError('Il nome è obbligatorio')
      return
    }
    if (!form.slug.trim()) {
      setSaveError('Lo slug è obbligatorio')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      const permissions = {
        canSeeAllContracts: form.canSeeAllContracts,
        canSeeSubordinates: form.canSeeSubordinates,
        canSeeRealAmounts: form.canSeeRealAmounts,
        canSeeTotalCommissions: form.canSeeTotalCommissions,
        canManageUsers: form.canManageUsers,
        canManageProducts: form.canManageProducts,
        canManagePipelines: form.canManagePipelines,
        canManageForms: form.canManageForms,
      }
      if (editId !== null) {
        const updateReq: {
          name: string; slug: string; sortOrder?: number; description?: string; color?: string
        } & typeof permissions = {
          name: form.name,
          slug: form.slug,
          sortOrder: form.sortOrder,
          ...permissions,
        }
        if (form.description) updateReq.description = form.description
        if (form.color) updateReq.color = form.color
        await userCategoriesApi.update(editId, updateReq)
      } else {
        const req: {
          name: string; slug: string; sortOrder?: number; description?: string; color?: string
        } & typeof permissions = {
          name: form.name,
          slug: form.slug,
          sortOrder: form.sortOrder,
          ...permissions,
        }
        if (form.description) req.description = form.description
        if (form.color) req.color = form.color
        await userCategoriesApi.create(req)
      }
      closeModal()
      void load()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Errore durante il salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(cat: UserCategory) {
    if (!(await askConfirm({ title: 'Elimina categoria', message: `Eliminare la categoria "${cat.name}"?`, confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await userCategoriesApi.delete(cat.id)
      void load()
    } catch (e) {
      snackbar.show({ message: e instanceof Error ? e.message : "Errore durante l'eliminazione", tone: 'error' })
    }
  }

  return (
    <Page>
      <PageHeader
        title="Categorie utenti"
        icon="admin_panel_settings"
        count={items.length > 0 ? items.length : undefined}
        actions={
          <Button variant="filled" icon="add" onClick={openCreate}>
            Nuova categoria
          </Button>
        }
      />

      {error && <Callout tone="error">{error}</Callout>}

      {rolesOn && <UserCategoryAdminPanel onChanged={() => void load()} />}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState icon="sell" title="Nessuna categoria trovata" />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>#</th>
                <th>Nome</th>
                <th>Slug</th>
                <th>Colore</th>
                <th>Ordine</th>
                <th>Sistema</th>
                <th>Stato</th>
                <th className="text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(cat => (
                <tr key={cat.id}>
                  <td className="text-muted md-num">{cat.id}</td>
                  <td className="md-typescale-emphasized-body-medium">{cat.name}</td>
                  <td>
                    <code className="md-typescale-body-small text-muted">{cat.slug}</code>
                  </td>
                  <td>
                    {cat.color ? (
                      <span
                        title={cat.color}
                        style={{
                          display: 'inline-block',
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: cat.color,
                          border: '1px solid hsl(var(--md-outline-variant))',
                        }}
                      />
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-muted md-num">{cat.sortOrder ?? 0}</td>
                  <td>
                    {cat.isSystem ? (
                      <StatusPill tone="primary" icon="lock">Sistema</StatusPill>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {cat.isActive ? (
                      <StatusPill tone="success">Attiva</StatusPill>
                    ) : (
                      <StatusPill tone="neutral">Inattiva</StatusPill>
                    )}
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end align-items-center">
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(cat)} />
                      {cat.isSystem ? (
                        <span
                          className="text-muted d-inline-flex align-items-center px-1"
                          title="Categoria di sistema — non eliminabile"
                        >
                          <Icon name="lock" size={20} />
                        </span>
                      ) : (
                        <IconButton icon="delete" size="sm" aria-label="Elimina" onClick={() => void handleDelete(cat)} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        title={editId !== null ? 'Modifica categoria' : 'Nuova categoria'}
        actions={
          <>
            <Button variant="text" onClick={closeModal}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving} >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {saveError && <Callout tone="error">{saveError}</Callout>}
          <FormSection title="Categoria">
            <TextField
              label="Nome *"
              type="text"
              supportingText="Nome categoria"
              value={form.name}
              onChange={e => handleNameChange(e.target.value)}
            />
            <TextField
              label="Slug *"
              type="text"
              className="font-monospace"
              supportingText="Generato automaticamente dal nome. Modificabile manualmente."
              value={form.slug}
              onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
            />
            <TextField
              id="cat-color"
              type="color"
              label="Colore"
              value={form.color}
              supportingText={form.color}
              onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
            />
            <TextField
              label="Ordine"
              type="number"
              min={0}
              value={form.sortOrder}
              onChange={e =>
                setForm(f => ({ ...f, sortOrder: parseInt(e.target.value, 10) || 0 }))
              }
            />
            <TextField
              className="md-span-full"
              label="Descrizione"
              multiline
              rows={2}
              supportingText="Descrizione opzionale"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
          </FormSection>
          <FormSection title="Permessi">
            {PERMISSION_FIELDS.map(({ key, label }) => (
              <Checkbox
                key={key}
                id={`perm-${key}`}
                label={label}
                checked={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))}
              />
            ))}
          </FormSection>
        </>
      </Dialog>
    </Page>
  )
}
